// apps/codeframe/src/motion/useFrameMotion.ts
import { useEffect } from 'react';
import type { RefObject } from 'react';
import type { Frame, MotionTimeline } from '../store/types';
import { MotionPlayer } from './player';
import { registerPlayer, unregisterPlayer, getPlayer } from './registry';
import { wireTriggers } from './drivers';
import { wireScroll } from './scrollDriver';
import { useCanvasStore } from '../store/canvas-store';
import { useTheme } from '../chrome/theme';

function motionOf(f: Frame): MotionTimeline | undefined {
  return 'motion' in f ? (f as { motion?: MotionTimeline }).motion : undefined;
}

/**
 * Start a native rAF clock that calls onTick(dtMs) each frame.
 * Returns a cancel function. Uses performance.now() for delta computation.
 * Native rAF chosen over motion-dom's `frame` scheduler because:
 *   - `frame`/`cancelFrame` are not exported from `motion` (re-exports framer-motion/dom
 *     which omits them); they live in `motion-dom` which is not a direct codeframe dep.
 *   - Native rAF is simpler, correct, and receives a DOMHighResTimeStamp directly.
 */
function startClock(onTick: (dtMs: number) => void): () => void {
  let rafId: number;
  let last = performance.now();

  const tick = (now: number) => {
    const dt = now - last;
    last = now;
    onTick(dt);
    rafId = requestAnimationFrame(tick);
  };

  rafId = requestAnimationFrame(tick);

  return () => cancelAnimationFrame(rafId);
}

export function useFrameMotion(ref: RefObject<HTMLElement>, frame: Frame): void {
  // Instances inherit their Main's timeline read-only (like text slots): the
  // resolved instance DOM carries the same persisted cf-ids as the Main, so the
  // Main's targets resolve against it. Re-subscribes when the Main's motion edits.
  const inherited = useCanvasStore((s) => {
    if (frame.kind !== 'instance' && frame.kind !== 'tsxInstance') return undefined;
    const main = s.doc.frames.find((f) => f.id === frame.mainId);
    if (!main) return undefined;
    if (frame.kind === 'instance') return main.kind === 'main' ? main.motion : undefined;
    return main.kind === 'tsxMain' ? main.motion : undefined;
  });
  const tl = motionOf(frame) ?? inherited;

  // Re-resolve theme-token color keyframes (var(--color-*)) when the theme
  // changes, so animated colors follow the active theme + light/dark toggle.
  const activeThemeId = useCanvasStore((s) => s.doc.activeThemeId);
  const themeMode = useTheme((s) => s.theme);
  const ownThemeId = 'themeId' in frame ? (frame as { themeId?: string }).themeId : undefined;
  useEffect(() => {
    getPlayer(frame.id)?.refreshTheme();
  }, [frame.id, activeThemeId, themeMode, ownThemeId]);

  useEffect(() => {
    const root = ref.current;
    if (!root || !tl || tl.tracks.length === 0) return;

    const player = new MotionPlayer(tl, root);
    registerPlayer(frame.id, player);
    const offTriggers = wireTriggers(root, tl, player);

    let delayTimer: ReturnType<typeof setTimeout> | undefined;
    if (tl.playback.autoplay) {
      const start = () => player.play();
      if (tl.playback.autoplay.delay) {
        delayTimer = setTimeout(start, tl.playback.autoplay.delay);
      } else {
        start();
      }
    }
    player.seek(0); // initial paint at t=0

    let stopScroll: (() => void) | undefined;
    if (tl.playback.scroll) {
      stopScroll = wireScroll(player, root, tl.playback.scroll);
    }

    const stopClock = startClock((dtMs) => player.tick(dtMs));

    return () => {
      stopClock();
      if (delayTimer) clearTimeout(delayTimer);
      offTriggers();
      stopScroll?.();
      player.destroy();
      unregisterPlayer(frame.id);
    };
  }, [ref, frame.id, tl]);
}
